import { EditOutlined } from '@ant-design/icons';
import { Button, Input, Tooltip, Typography } from 'antd';
import {
  useState,
  useRef,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import ProTable, { ProColumns, ActionType } from '@ant-design/pro-table';
import { SorterResult } from 'antd/es/table/interface';
import { SaveForm } from '../../components/common/ABM';
import TableSearchInput from '../../components/common/TableSearchInput';
import { ABM, Authorization, Tools } from '../../shared';
import { useCustomMessage } from '../../hooks';
import { ContextApp } from '../../contexts/ContextApp';
import { EnumsValues } from '../../enums/EnumsValues';
import useProTableForMobile from '../../hooks/useProTableForMobile';
import {
  ITenantSetting,
  ITenantSettingValue,
} from '../../interfaces/TenantSetting';
import { ParamsType } from '@ant-design/pro-provider';
import { getPaginationArgs } from '../../shared/getPaginationArgs';
import { useCustomMutation } from '../../hooks/apollo/ApolloCustomHooks';
import { Mutation } from '../../services/graphql/mutation';
import { useTenantSettingsRequest } from './useTenantSettingsRequest';
import { useScopedMutation } from '../../hooks/useScopedMutation';
import './TenantSettingPage.less';

interface IUpdateTenantSettingParams {
  description?: string;
  setting_value?: string;
}

interface IUpdateTenantSettingArgs {
  tenant_id: number;
  setting_id: number;
  value: string;
}

const LIST_FILTER = new Set(['name', 'description']);
const LIST_SORTER = new Set(['name']);

interface TenantSettingQueryVariables {
  filter?: Record<string, unknown>;
  orderBy?: { direction?: string; field?: string };
  searchText?: string;
  skip?: number;
  take?: number;
  [key: string]: unknown;
}

const TenantSettingPage = () => {
  const {
    messageUpdating,
    messageUpdateSuccess,
    messageError,
    getErrorMessage,
  } = useCustomMessage();
  const { functions, selectedTenantId, t, user } = useContext(ContextApp);
  const [sorter, setSorter] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [updateModalVisible, setUpdateModalVisible] = useState<boolean>(false);
  const [editForm, setEditForm] = useState<ITenantSetting>(
    {} as ITenantSetting,
  );

  const TITLE_UPDATE_FORM = t('abm.titleUpdateForm', {
    replace: { entity: t('entity.config') },
  });
  const INPUT_SEARCH_PLACEHOLDER = t('abm.inputSearchPlaceholder');

  const [createTenantSettingValueMutation] = useCustomMutation<{
    createTenantSettingValue: ITenantSettingValue;
  }>(Mutation.createTenantSettingValue);

  const [updateTenantSettingValueMutation] = useCustomMutation<{
    updateTenantSettingValue: ITenantSettingValue;
  }>(Mutation.updateTenantSettingValue);

  const actionRef = useRef<ActionType | undefined>(undefined);
  const variables = useRef<TenantSettingQueryVariables>({});
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const previousTenantId = useRef(selectedTenantId);
  const formSession = useMemo(() => ({}), [updateModalVisible, editForm.id]);
  const { runMutation, formLoading } = useScopedMutation(
    user?.id,
    selectedTenantId,
    formSession,
  );
  const fetchTenantSettingsPage = useTenantSettingsRequest(
    selectedTenantId,
    user?.id,
  );

  const { mobileOnSizeChangeProTable, changeView, showComponent } =
    useProTableForMobile({
      layout: 'horizontal',
    });

  const { TextArea } = Input;

  const handleSearch = (value: string) => {
    searchTextRef.current = value;
    setSearchText(value);
    const reloadAndRest = actionRef.current?.reloadAndRest;
    if (reloadAndRest) {
      reloadAndRest().catch((error: unknown) =>
        messageError({ context: 'TenantSettingPage.reloadAndRest', error }),
      );
    }
  };

  useEffect(() => {
    if (previousTenantId.current === selectedTenantId) return;
    previousTenantId.current = selectedTenantId;
    setUpdateModalVisible(false);
    setEditForm({} as ITenantSetting);
    searchTextRef.current = '';
    setSearchText('');
    setSorter('');
  }, [selectedTenantId]);

  const request = async (
    params: ParamsType & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
  ) => {
    variables.current = {};
    const search = ABM.valuesResult(params) as Record<string, unknown> & {
      sorter?: Record<string, string | undefined>;
    };

    if (searchTextRef.current) {
      variables.current.searchText = searchTextRef.current;
    }

    LIST_FILTER.forEach((element) => {
      try {
        if (search[element]) {
          variables.current.filter ??= {};
          variables.current.filter[element] = search[element];
        }
      } catch {
        // A filter can be absent from the current table parameters.
      }
    });

    LIST_SORTER.forEach((element) => {
      try {
        const sortOrder = search.sorter?.[element];
        if (sortOrder) {
          variables.current.orderBy ??= {};
          variables.current.orderBy.direction =
            Tools.getTypeOrderByTableSortParam(sortOrder);
          variables.current.orderBy.field = element;
        }
      } catch {
        // A sorter can be absent from the current table parameters.
      }
    });

    const { skip, take } = getPaginationArgs(
      params.pageSize || 20,
      params.current,
    );

    variables.current.skip = skip;
    variables.current.take = take;

    const result = await fetchTenantSettingsPage(variables.current);
    if (result.success) {
      return {
        current: params.current,
        data: result.settings,
        pageSize: params.pageSize,
        success: true,
        total: result.total,
      };
    }
    return {
      current: params.current,
      data: [],
      pageSize: params.pageSize,
      success: false,
      total: 0,
    };
  };

  const updateTenantSetting = useCallback(
    async (value: IUpdateTenantSettingParams) => {
      if (selectedTenantId == null || !editForm.id || !value.setting_value)
        return;

      const variables: IUpdateTenantSettingArgs = {
        tenant_id: selectedTenantId,
        setting_id: editForm.id,
        value: value.setting_value,
      };

      await runMutation<unknown>({
        channel: 'form',
        onStart: () =>
          messageUpdating({
            context: 'TenantSettingPage.updateTenantSetting.3',
            message: t('entity.config'),
          }),
        send: () =>
          !editForm.tenant_settings_current_value?.length
            ? createTenantSettingValueMutation({ variables })
            : updateTenantSettingValueMutation({ variables }),
        onSuccess: (_result, formIsCurrent) => {
          messageUpdateSuccess({
            context: 'TenantSettingPage.updateTenantSetting.4',
          });
          actionRef.current
            ?.reload()
            .catch((error: unknown) =>
              messageError({ context: 'TenantSettingPage.reloadTable', error }),
            );
          if (formIsCurrent) setUpdateModalVisible(false);
        },
        onError: (error) =>
          messageError({
            error,
            context: 'TenantSettingPage.updateTenantSetting.5',
            message: getErrorMessage(error),
          }),
      });
    },
    [
      createTenantSettingValueMutation,
      editForm,
      getErrorMessage,
      messageError,
      messageUpdateSuccess,
      messageUpdating,
      runMutation,
      selectedTenantId,
      t,
      updateTenantSettingValueMutation,
    ],
  );

  const columns = useCallback(
    (): ProColumns<ITenantSetting>[] => [
      {
        title: Tools.capitalize(t('entity.id')),
        dataIndex: 'id',
        hideInTable: true,
        hideInSearch: true,
        hideInForm: true,
        fixed: 'left',
        width: 1,
      },
      {
        title: Tools.capitalize(t('entity.key')),
        dataIndex: 'name',
        hideInSearch: true,
        hideInForm: true,
        hideInTable: false,
        sorter: true,
        defaultSortOrder: 'ascend',
        render: (_, record) => (
          <Tooltip placement="top" title={record?.name || ''}>
            <div>{record?.name || ''}</div>
          </Tooltip>
        ),
      },
      {
        title: Tools.capitalize(t('entity.value')),
        dataIndex: 'setting_value',
        hideInSearch: true,
        hideInTable: false,
        render: (_, record) => {
          const value = record?.tenant_settings_current_value?.length
            ? record?.tenant_settings_current_value[0].value
            : record.default_value;

          return (
            <Tooltip placement="top" title={value || ''}>
              <div>{value || ''}</div>
            </Tooltip>
          );
        },
        renderFormItem: () => (
          <Input
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.value') },
            })}
            maxLength={200}
          />
        ),
        formItemProps: {
          rules: [
            {
              required: true,
              message: t('validation.required'),
            },
          ],
        },
      },
      {
        title: Tools.capitalize(t('entity.description')),
        dataIndex: 'description',
        hideInSearch: true,
        hideInForm: false,
        hideInTable: false,
        render: (_, record) => record?.description || '',
        renderFormItem: () => (
          <TextArea
            rows={4}
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.description') },
            })}
            maxLength={200}
            disabled
          />
        ),
      },

      {
        title: t('abm.operations'),
        dataIndex: 'option',
        valueType: 'option',
        fixed: 'right',
        width: 50,
        render: (_, record) => (
          <>
            {Authorization.security(
              functions,
              EnumsValues.Functions.UpdateTenantSetting,
            ) && (
              <Tooltip
                key="edit_setting"
                title={t('abm.editTooltip', {
                  replace: { entity: t('entity.config') },
                })}
              >
                <EditOutlined
                  className="pointer"
                  onClick={() => {
                    setUpdateModalVisible(true);
                    setEditForm(record);
                  }}
                />
              </Tooltip>
            )}
          </>
        ),
      },
    ],
    [],
  );

  const LIST_FILTER_NAMES = columns()
    .filter(
      (value) =>
        typeof value.dataIndex === 'string' &&
        LIST_FILTER.has(value.dataIndex) &&
        value.hideInTable === false,
    )
    .map((element) => element.title)
    .filter((title): title is string => typeof title === 'string');

  return (
    <main className="tenant-setting-page">
      <header className="tenant-setting-page__intro">
        <Typography.Title level={1}>
          {t('pages.tenantConfiguration')}
        </Typography.Title>
        <Typography.Paragraph>
          {t('tenantSettingPage.description')}
        </Typography.Paragraph>
      </header>
      <ProTable<ITenantSetting>
        key={selectedTenantId}
        onSizeChange={mobileOnSizeChangeProTable}
        components={{
          table: showComponent(),
        }}
        headerTitle={null}
        actionRef={actionRef}
        rowKey="id"
        search={false}
        onChange={(_, _filter, _sorter) => {
          const sorterResult = _sorter as SorterResult<ITenantSetting>;
          if (sorterResult.field) {
            setSorter(`${sorterResult.field}_${sorterResult.order}`);
          }
        }}
        onReset={() => {
          searchTextRef.current = '';
          setSearchText('');
        }}
        params={{
          sorter,
        }}
        toolBarRender={() => []}
        toolbar={{
          search: (
            <div className="content-search-table">
              <TableSearchInput
                value={searchText}
                placeholder={INPUT_SEARCH_PLACEHOLDER}
                tooltip={`${Tools.capitalize(
                  t('action.searchBy'),
                )} ${LIST_FILTER_NAMES.join(', ')}`}
                resetKey={selectedTenantId}
                onSearch={handleSearch}
              />
            </div>
          ),
          actions: [
            <Button
              key="change-view"
              onClick={changeView}
              className="buttonChangeProTableView"
            >
              {Tools.capitalize(t('action.changeView'))}
            </Button>,
          ],
        }}
        request={async (params, sorter, filter) =>
          request({ ...params, sorter, filter })
        }
        columns={columns()}
      />

      {editForm && (
        <SaveForm
          loading={formLoading}
          title={TITLE_UPDATE_FORM}
          modalVisible={updateModalVisible}
          values={{
            setting_value: editForm.tenant_settings_current_value?.length
              ? editForm.tenant_settings_current_value[0].value
              : editForm.default_value,
            description: editForm.description,
          }}
          columns={columns()}
          onOk={async (value: IUpdateTenantSettingParams) =>
            updateTenantSetting({
              description: value.description ?? undefined,
              setting_value: value.setting_value,
            })
          }
          onCancel={() => {
            setUpdateModalVisible(false);
            setEditForm({} as ITenantSetting);
          }}
        />
      )}
    </main>
  );
};

export default TenantSettingPage;
